import React, { useEffect } from 'react'
import { View, ScrollView } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Button, Text, IconName } from '@components/base'
import {
  CommonAuthorizedStackParamList,
  RootStackParamList,
  ScreenAndProps,
} from '@src/navigation/types'
import { NavigationContainer } from '@src/screens/Common/containers'
import { CoachesCoachingKind, EhrAppointmentAttendanceChargeKind, Product } from '@src/types'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import { useGoBack } from '@src/utils'
import { appointmentKindSelector } from '@src/selectors/app'
import { getCoachingKindDescriptor } from '@src/utils/coach'
import {
  ehrSuggestedAvailabilitySelector,
  insurancePolicySelector,
  priorityOrderedCoachAssignmentsSelector,
} from '@screens/NutritionistHub/models/nutritionistHub.selectors.ts'
import { useGetRequiredPolicyConsents } from '@src/screens/PolicyConsents/hooks/useGetRequiredPolicyConsents'
import Storage from '@src/utils/storage'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { ProductIcon } from '../types/types'
import { usePurchasedAppointment } from '../utils/hooks/usePurchasedAppointment'

const iconNameForProduct = (product: Pick<Product, 'icon'>): IconName | undefined => {
  switch (product.icon) {
    case ProductIcon.Question:
      return 'seal-question'
    case ProductIcon.Goals:
      return 'list-checks'
    case ProductIcon.Nutrition:
      return 'bowl-food'
    case ProductIcon.VideoCall:
      return 'video-camera'
    default:
      return
  }
}

export const DietitianAddonOrderConfirmed = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const goBack = useGoBack()
  const dispatch = useDispatch()
  const {
    params: {
      product,
      groupedProducts,
      goBackOnSuccess,
      suggestedCallFlow,
      meetingAt,
      coachId: coachIdFromParams,
    },
  } = useRoute<RouteProp<CommonAuthorizedStackParamList, 'DietitianAddonOrderConfirmed'>>()

  const suggestedAvailability = useSelector(ehrSuggestedAvailabilitySelector)
  const insurancePolicy = useSelector(insurancePolicySelector)
  const coachAssignments = useSelector(priorityOrderedCoachAssignmentsSelector) || []

  const { getRequiredPolicyConsents } = useGetRequiredPolicyConsents()
  useLazyScreenPreload('SupportHub')

  const titleIcon = iconNameForProduct(product)

  const {
    pendingAppointment,
    pendingAppointmentKind,
    appointmentLoadingError,
    isSchedulingRequired,
    stopPolling,
  } = usePurchasedAppointment({ product })

  const pendingAppointmentId = pendingAppointment?.id
  const appointmentKind = useSelector(appointmentKindSelector(pendingAppointmentKind))
  const coachingKind = appointmentKind?.coachingKind.key
  const hasAssignedCoach = !!coachAssignments.find(
    (assignment) => assignment.coachingKind.key === coachingKind,
  )?.coach
  const shouldChooseCoachBeforeScheduling = !!coachingKind && !hasAssignedCoach
  const coachDescriptor = getCoachingKindDescriptor(coachingKind)
  const coachTitle = coachDescriptor.capitalized.toString()
  const coachPluralTitle = coachDescriptor.capitalized.plural.toString()
  const showFilters = coachingKind !== CoachesCoachingKind.StressManagement

  const handleDonePress = () => {
    if (goBackOnSuccess) {
      goBack()
      return
    }

    if (!isSchedulingRequired) {
      navigation.navigate('Subscriptions')
      return
    }

    if (shouldChooseCoachBeforeScheduling) {
      navigation.navigate('CallSchedulingSeeAllCoachesScreen', {
        coachingKind,
        navigationTitle: `Choose Your ${coachTitle}`,
        title: `Available ${coachPluralTitle}`,
        loadMoreButtonText: 'Load More',
        showFilters,
        callSchedulingParams: {
          appointmentKind: pendingAppointmentKind,
          pendingAppointmentId,
          allowBackNavigation: true,
        },
      })
      return
    }

    navigation.navigate('CallScheduling', {
      appointmentKind: pendingAppointmentKind,
      pendingAppointmentId,
      allowBackNavigation: true,
    })
  }

  const handleErrorPress = () => {
    navigation.navigate('SupportHub')
  }

  const handleBackToNutritionistHub = () => {
    navigation.navigate('NutritionistHub')
  }

  const getDate = () => {
    if (suggestedCallFlow && suggestedAvailability) {
      return suggestedAvailability.meetingAt
    }

    if (meetingAt) {
      return meetingAt
    }

    return undefined
  }

  const appointmentDate = getDate()
  const insurancePolicyId = insurancePolicy?.id

  useEffect(() => {
    if (!insurancePolicyId) {
      return
    }

    // prevent insurance policy rejection modal from showing
    const modalKey = `${Storage.INSURANCE_POLICY_REJECTED_MODAL_VISITED_KEY}_${insurancePolicyId}`
    Storage.set(modalKey, true)
  }, [insurancePolicyId])

  const getCoachId = () => {
    if (suggestedCallFlow && suggestedAvailability) {
      return suggestedAvailability.providerId
    }

    if (coachIdFromParams) {
      return coachIdFromParams
    }

    return undefined
  }

  const coachId = getCoachId()

  // Once pending appointment is present we can stop polling and proceed
  useEffect(() => {
    if (!pendingAppointmentId) {
      return
    }

    stopPolling()

    if (!appointmentDate) {
      return
    }

    dispatch({
      type: 'nutritionistHub/bookAppointment',
      payload: {
        appointmentKind: pendingAppointmentKind,
        pendingAppointmentId,
        date: appointmentDate,
        coachId,
        attendanceChargeKind: EhrAppointmentAttendanceChargeKind.BsPaid,
      },
      success: async () => {
        dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })
        dispatch({ type: 'nutritionistHub/fetchEhrSuggestedAvailability' })
        dispatch({ type: 'nutritionistHub/fetchInsurancePolicy' })

        const nextScreen: ScreenAndProps = {
          screen: 'Drawer',
          params: { screen: 'Dashboard', params: { screen: 'NutritionistHub' } },
        }
        const requiredPolicies = await getRequiredPolicyConsents()

        if (requiredPolicies.length > 0) {
          navigation.replace('PolicyConsent', { policy: requiredPolicies[0], nextScreen })
        } else {
          navigation.replace(nextScreen.screen, nextScreen.params)
        }
      },
      failure: () => {
        navigation.replace('CallScheduling', {
          appointmentKind: pendingAppointmentKind,
          pendingAppointmentId,
          allowBackNavigation: true,
          errorMessage:
            'The suggested appointment time is no longer available. Please choose a different time slot',
        })
      },
    })
  }, [
    appointmentDate,
    coachId,
    dispatch,
    getRequiredPolicyConsents,
    navigation,
    pendingAppointmentId,
    pendingAppointmentKind,
    stopPolling,
  ])

  const { title, description, details } = groupedProducts ?? product

  if (
    isSchedulingRequired &&
    (!pendingAppointment || (suggestedCallFlow && suggestedAvailability))
  ) {
    return (
      <LoadingContainer
        loadingMessage="Confirming your purchase"
        errorMessage="Can't confirm your purchase"
        error={appointmentLoadingError}
      >
        <Button
          type="outline"
          size="block"
          accessibilityLabel="Back to Nutritionist Hub"
          onPress={handleBackToNutritionistHub}
          style={styles.doneButton}
        >
          Back to Nutritionist Hub
        </Button>
        <Button
          type="primary"
          size="block"
          accessibilityLabel="Continue"
          onPress={handleErrorPress}
        >
          Contact Support
        </Button>
      </LoadingContainer>
    )
  }

  return (
    <NavigationContainer title={title}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <SafeAreaView style={styles.container} edges={['bottom']}>
          <View style={styles.confirmationContainer}>
            <View>
              <Icon name="confetti" style={styles.congratsIcon} />
              <Text type="title-3" style={styles.congratsTitle}>
                Success!
              </Text>
              <Text type="large" bold style={styles.congratsSubtitle}>
                You have purchased a new nutrition coaching service:
              </Text>
            </View>
            <View style={styles.productContainer}>
              <View style={styles.productTitle}>
                {titleIcon && <Icon name={titleIcon} style={styles.productIcon} />}
                <Text type="large" bold style={styles.productName}>
                  {title}
                </Text>
              </View>
              <Text type="regular" style={styles.productDescription}>
                {description ?? ''}
              </Text>
            </View>
            {details && !isSchedulingRequired && (
              <View style={styles.nextStepsContainer}>
                <Text type="regular" bold style={styles.nextStepsTitle}>
                  WHAT'S NEXT?
                </Text>
                {details.map((detailsText) => (
                  <Text type="small" key={detailsText} style={styles.nextStepsDescription}>
                    {detailsText}
                  </Text>
                ))}
              </View>
            )}
          </View>
          <Button
            type="primary"
            size="block"
            accessibilityLabel="Done"
            style={styles.doneButton}
            onPress={handleDonePress}
          >
            {isSchedulingRequired && !appointmentDate ? 'Schedule call' : 'Done'}
          </Button>
        </SafeAreaView>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  contentContainer: {
    flexGrow: 1,
    flexShrink: 0,
    paddingHorizontal: 16,
  },
  confirmationContainer: {
    flexGrow: 1,
    flexShrink: 0,
    justifyContent: 'center',
  },
  congratsIcon: {
    alignSelf: 'center',
    width: 48,
    height: 48,
  },
  congratsTitle: {
    marginTop: 24,
    textAlign: 'center',
  },
  congratsSubtitle: {
    marginTop: 16,
    textAlign: 'center',
  },
  productContainer: {
    marginTop: 16,
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'theme.primary.base',
    backgroundColor: 'theme.surface.base2',
  },
  productTitle: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  productIcon: {
    marginRight: 8,
    width: 16,
    height: 16,
  },
  productName: {
    color: 'theme.primary.base',
  },
  productDescription: {
    marginTop: 4,
    color: 'theme.text.secondary',
  },
  nextStepsContainer: {
    marginTop: 32,
  },
  nextStepsTitle: {
    color: 'theme.primary.base',
  },
  nextStepsDescription: {
    marginTop: 2,
  },
  doneButton: {
    marginVertical: 16,
  },
})
